<template>
	<div v-if="errorTxt">
		<p>{{ errorTxt }}</p>
	</div>
	<div v-else>
		<div class="d-flex justify-content-end" v-if="isJobCompleted()">
			<button class="btn btn-sm btn-secondary" :disabled="loading" @click="retryProgress">
				<div v-if="loading" class="spinner-border spinner-border-sm" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
				<div v-else>
					<i class="fas fa-redo"></i>
				</div>
			</button>
		</div>
		<div v-for="stat in progressStats" :key="stat.stage_name" class="mb-5">
			<h5 class="text-center fw-bold text-uppercase">{{ stat.stage_name }}</h5>
			<div style="overflow:auto">
				<table class="table table-bordered table-responsive mb-0 table-nowrap">
					<colgroup v-if="stat.is_completed">
						<col style="width: 300px"> <!-- Started At -->
						<col style="width: 130px"> <!-- State -->
						<col style="width: 185px"> <!-- Duration -->
						<col style="width: 394px"> <!-- Tasks Progress -->
						<col style="width: 264px"> <!-- Total Retries -->
						<col style="width: 264px"> <!-- Input Records -->
						<col style="width: 263px"> <!-- Output Records 190-->
					</colgroup>
					<colgroup v-else>
						<col style="width: 300px"> <!-- Started At -->
						<col style="width: 130px"> <!-- State -->
						<col style="width: 185px"> <!-- Duration -->
						<col style="width: 394px"> <!-- Tasks Progress -->
						<col style="width: 300px"> <!-- Total Retries -->
						<col style="width: 491px"> <!-- Estimate completion Time -->
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="text-wrap">Started At</th>
							<th scope="col" class="text-wrap">State</th>
							<th scope="col" class="text-wrap">Duration</th>
							<th scope="col" class="text-wrap">Tasks Progress</th>
							<th scope="col" class="text-wrap">Total Retries</th>
							<th scope="col" class="text-wrap" v-if="!stat.is_completed">Estimate completion time</th>
							<th scope="col" class="text-wrap" v-if="stat.is_completed">Input Records</th>
							<th scope="col" class="text-wrap" v-if="stat.is_completed">Output Records</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td class="text-wrap">{{ DateTimeFormat(stat.start_time) }}</td>
							<td class="text-wrap" v-if="stat.state == 'Succeeded'"><span class="text-success">●</span> Succeeded</td>
							<td class="text-wrap" v-else-if="stat.state == 'Failed'"><span class="text-danger">●</span> Failed</td>
							<td class="text-wrap" v-else-if="stat.state == 'Running'"><span class="text-primary">●</span> Running</td>
							<td class="text-wrap" v-else-if="stat.state == 'Pending'"><span class="text-warning">●</span> Pending</td>
							<td class="text-wrap" v-else-if="stat.state == 'Cancelled'"><span class="text-secondary">●</span> Cancelled</td>
							<td class="text-wrap" v-else>
								<div v-if="stat.is_completed"><span class="text-success">●</span> Completed</div>
								<div v-else><span class="text-primary">●</span> In Progress</div>
							</td>
							<td class="text-wrap">{{ secondsToHourMinSec(stat.duration) }}</td>
							<td class="text-wrap">
								<div class="justify-content-between d-flex">
									<span class="px-2">Completed: <span class="badge rounded-pill bg-success">{{ stat.completed_tasks }}</span></span> <span class="px-2"> In Progress: <span class="badge rounded-pill bg-primary">{{ stat.in_progress_tasks }}</span></span>  <span>Total: <span class="badge rounded-pill bg-light text-dark">{{ stat.total_tasks }}</span></span>
								</div>
								<div v-if="stat.total_tasks" class="progress mt-2" >
									<div class="
											progress-bar
											progress-bar-striped
											bg-success
										" role="progressbar" aria-label="Animated striped example" :aria-valuenow="stat.total_tasks"
										aria-valuemin="0" :aria-valuemax="stat.total_tasks" :style="`width:${calcPercentage(
											stat.completed_tasks,
											stat.total_tasks
										)}`">
										{{ stat.completed_tasks }} 
									</div>
									<div class="
											progress-bar
											progress-bar-striped
											progress-bar-animated
										" role="progressbar" aria-label="Animated striped example" :aria-valuenow="stat.in_progress_tasks"
										aria-valuemin="0" :aria-valuemax="stat.total_tasks" :style="`width:${calcPercentage(
											stat.in_progress_tasks,
											stat.total_tasks
										)}`">
										{{ stat.in_progress_tasks }}
									</div>
									<div class="
											progress-bar
											bg-light text-dark
										" role="progressbar" aria-label="Animated striped example" :aria-valuenow="stat.in_progress_tasks"
										aria-valuemin="0" :aria-valuemax="stat.total_tasks" :style="`width:${calcPercentage(
											stat.total_tasks-(stat.in_progress_tasks + stat.completed_tasks),
											stat.total_tasks
										)}`">
										{{ stat.total_tasks-(stat.in_progress_tasks + stat.completed_tasks)  }}
									</div>							 
								</div>
							</td>
							<td class="text-wrap">{{stat.total_retries}}</td>
							<td class="text-wrap" v-if="!stat.is_completed">{{ secondsToHourMinSec(stat.eta) }}</td>
							<td class="text-wrap" v-if="stat.is_completed">{{ stat.input_record_count }}</td>
							<td class="text-wrap" v-if="stat.is_completed">{{ stat.output_record_count }}</td>
						</tr>
						<tr v-for="(executorStat, index) in stat.executor_groups" :key="index">
							<td colspan="8" class="p-0">
								<table class="table mb-0 w-100 table-bordered">
									<colgroup v-if="hasRunningExecutors(executorStat)">
										<col style="width: 300px"> <!-- Executor Name -->
                                        <col style="width: 130px"> <!-- State -->
                                        <col style="width: 181px"> <!-- Created At -->
                                        <col style="width: 181px"> <!-- Started At -->
                                        <col style="width: 181px"> <!-- Ended At -->
                                        <col style="width: 181px"> <!-- Pending Duration -->
                                        <col style="width: 181px"> <!-- Duration -->
										<col style="width: 112px"> <!-- Tasks In Progress -->
                                        <col style="width: 120px"> <!-- Tasks Completed -->
                                        <col style="width: 123px"> <!-- CPU Utilization -->
                                        <col style="width: 110px"> <!-- Max Memory -->
									</colgroup>
									<colgroup v-else>
										<col style="width: 300px"> <!-- Executor Name 1800 -->
                                        <col style="width: 130px"> <!-- State -->
                                        <col style="width: 194px"> <!-- Created At -->
                                        <col style="width: 194px"> <!-- Started At -->
                                        <col style="width: 194px"> <!-- Ended At -->
                                        <col style="width: 194px"> <!-- Pending Duration -->
                                        <col style="width: 194px"> <!-- Duration -->
                                        <col style="width: 137px"> <!-- Tasks Completed -->
                                        <col style="width: 138px"> <!-- CPU Utilization -->
                                        <col style="width: 125px"> <!-- Max Memory -->
									</colgroup>
									<thead data-bs-toggle="collapse"
										:data-bs-target="`#accordion-${stat.stage_name}-${index}`" class="collapsed">
										<tr role="button">
											<th scope="col" colspan="11" class="text-center">
												<i class="fa fa-chevron-up float-start"></i>
												Executor Group {{ index }}:- <span style="text-transform: none;">CPUs: </span>{{ executorStat.cpus }}, Memory: {{
													formatBytes(executorStat.memory) }}, Workers: {{ executorStat.workers }},
												Count: {{ executorStat.count }}
											</th>
										</tr>
										<tr :id="`accordion-${stat.stage_name}-${index}`"
											class="accordion-collapse collapse">
											<th scope="col" class="text-wrap">Executor Name</th>
											<th scope="col" class="text-wrap">State</th>
											<th scope="col" class="text-wrap">Created At</th>
											<th scope="col" class="text-wrap">Started At</th>
											<th scope="col" class="text-wrap">Ended At</th>
											<th scope="col" class="text-wrap">Pending Duration</th>
											<th scope="col" class="text-wrap">Duration</th>
											<th scope="col" class="text-wrap" v-if="hasRunningExecutors(executorStat)">Tasks In Progress</th>
											<th scope="col" class="text-wrap">Tasks Completed</th>
											<th scope="col" class="text-wrap">CPU Utilization</th>
											<th scope="col" class="text-wrap">Max Memory</th>
										</tr>
									</thead>
									<tbody :id="`accordion-${stat.stage_name}-${index}`"
										class="accordion-collapse collapse">
										<tr v-for="(executor, key, index) in executorStat.executors" :key="index">
											<td class="text-wrap">{{ key }}</td>
											<td class="text-wrap" v-html="addStatusIcon(executor.state)"></td>
											<td class="text-wrap">{{ DateTimeFormat(executor.created_time) }}</td>
											<td class="text-wrap">{{ DateTimeFormat(executor.start_time) }}</td>
											<td class="text-wrap">{{ DateTimeFormat(executor.end_time) }}</td>
											<td class="text-wrap">{{ secondsToHourMinSec(executor.pending_duration) }}</td>
											<td class="text-wrap">{{ secondsToHourMinSec(executor.duration) }}</td>
											<td class="text-wrap" v-if="hasRunningExecutors(executorStat)">{{ executor.in_progress_tasks || 0 }}</td>
											<td class="text-wrap">{{ executor.completed_tasks }}</td>
											<td class="text-wrap">{{ (executor.cpu_utilization) ? executor.cpu_utilization.toFixed(1)+" %" : "" }}</td>
											<td class="text-wrap">{{ executor.max_memory ? formatBytes(executor.max_memory) : "" }}</td>
										</tr>
									</tbody>
								</table>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions } from "vuex";
import { DateTimeFormat, formatBytes, secondsToHourMinSec } from '../../utils/commonFunction';
export default {
	data() {
		return {
			errorTxt: false,
			breakCheck: false,
			loading: false,
		};
	},
	props: ["sandboxName", "jobName", "executionId", "status", "retryProgressTrigger"],
	computed: {
		progressStats() {
			if (this.$store.state.sandbox.jobExecutionProgress && this.$store.state.sandbox.jobExecutionProgress.stages) {
				const stages = this.$store.state.sandbox.jobExecutionProgress.stages;
				const filteredstages = stages.reverse();
				return filteredstages
			} else {
				return [];
			}
		},
	},
	methods: {
		DateTimeFormat,
		formatBytes,
		secondsToHourMinSec,
		...mapActions({
			getJobExecutionProgress: "sandbox/getJobExecutionProgress",
		}),
		calcPercentage(a, b) {
			const percent = (a / b) * 100;
			return percent.toFixed(2) + "%";
		},
		hasRunningExecutors(data) {
			const executors = data.executors || {};
			return Object.values(executors).some(
				executor => executor && executor.state?.toLowerCase() === 'running'
			);
		},
		async handleGetProgress() {
			if (this.breakCheck)
				return;

			let response;
			try {
				response = await this.getJobExecutionProgress({
					sandboxName: this.sandboxName,
					jobName: this.jobName,
					executionId: this.executionId,
				});
				if (this.isJobCompleted()) {
					this.breakCheck = true
				}
				setTimeout(() => {
					this.handleGetProgress();
				}, 5000)
			} catch (e) {
				if (e.response && e.response.status)
					this.errorTxt = (e.response.status == 204) ? e.response.data.error : "Progress stats not available";
				this.breakCheck = true;
			}
		},
		isJobCompleted() {
			if (this.status && (this.status == 'failed' || this.status == 'succeeded' || this.status == 'cancelled'))
				return true
			else return false
		},
		async retryProgress() {
			this.loading = true
			this.breakCheck = false
			this.errorTxt = ""
			await this.handleGetProgress()
			this.loading = false
		},
		addStatusIcon(status) {
			let statusStr = "";
			if(status.toLowerCase()==="succeeded"){
			statusStr = "<span class='text-success'>●</span> ";
			} else if(status.toLowerCase()==="failed"){
				statusStr = "<span class='text-danger'>●</span> ";
			} else if(status.toLowerCase()==="pending"){
				statusStr = "<span class='text-warning'>●</span> ";
			} else if(status.toLowerCase()==="running"){
				statusStr = "<span class='text-primary'>●</span> ";
			} else if(status.toLowerCase()==="cancelled"){
				statusStr = "<span class='text-secondary'>●</span> ";
			}

			return statusStr + status;
		}
	},
	watch: {
		retryProgressTrigger() {
			if (this.isJobCompleted()){
				this.retryProgress()
			}
		},
	},

	beforeMount() {
		this.handleGetProgress();

	},
	beforeUnmount() {
		this.breakCheck = true;
	}
};
</script>

<style scoped>
.collapsed .fa {
	transform: rotate(180deg);
}
.spinner-border {
	width: 1em;
	height: 1em;
}
table.table {
  table-layout: fixed;
}
.text-wrap {
  white-space: normal !important;
}
.table-nowrap {
  white-space: normal;
}
</style>
